import React from 'react';

import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Box,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Theme,
} from '@mui/material';
import _ from 'lodash';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import { FieldValues, Form } from 'react-hook-form';

import { OhsFormSettings } from 'global-components/form/services/OhsFormModels';
import OhsModuleFilter from 'dashboard/topFilter/filterfields/OhsModuleFilter';
import OhsWorkplaceFilter from 'dashboard/topFilter/filterfields/OhsWorkplaceFilter';
import OhsCheckboxFilter from 'dashboard/topFilter/filterfields/OhsCheckboxFilter';
import { SmallScreenWidthBreakpoint } from 'global-components/theme/OhsLayoutConstants';
import { OhsModuleSearchInputFields } from 'search/OhsSearchModels';
import ohsDebounce from 'global-services/utils/OhsDebounce';
import { ModuleType, TierType } from 'global-services/constants/OhsObject';
import OhsSortFilter from 'dashboard/topFilter/filterfields/OhsSortFilter';
import OhsUserLocalServices from 'user/OhsUserLocalServices';
import OhsMultipleSelectField from 'global-components/form/fields/OhsMultipleSelectField';
import OhsDateRangePickerNew from 'global-components/form/fields/OhsDateRangePickerNew';
import { ParamTypes } from 'global-services/register/OhsRegisterModels';
import { getTodayDateString } from 'global-services/utils/OhsDateServices';
import { OhsDateOccurredRecordField } from 'incident/models/OhsIncidentRecordField';
import { getModuleName } from 'user/OhsUserServices';

import OhsDateDueFilter from './filter-fields/OhsDateDueFilter';
import { returnSelectLabel, returnSelectOptions } from './utils';

const useStyles = makeStyles()((theme: Theme) => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px',
  },
  filterWrap: {
    marginTop: '0',
    padding: '0',
  },
  filterCheckboxes: {
    display: 'flex',
    justifyContent: 'start',
    flexWrap: 'wrap',
    margin: '10px 8px',
    [theme.breakpoints.down(SmallScreenWidthBreakpoint)]: {
      width: '100%',
    },
  },
  clearBtn: {
    background: theme.palette.custom.HEADER_BAR_BG_COLOR ?? '#d3d3d3',
    color: 'black',
    '&:hover': {
      background: theme.palette.custom.HEADER_BAR_BG_COLOR ?? '#fafafa',
    },
  },
}));

interface Props {
  formSubmit: (data: any) => void;
  ohsFormSettings: OhsFormSettings<FieldValues>;
  inputFields: OhsModuleSearchInputFields;
  moduleType?: ModuleType;
}

function OhsModuleSearchForm(props: Props) {
  const user = OhsUserLocalServices.getLocalOhsUser();
  const { classes } = useStyles();
  const { inputFields } = props;
  const moduleName = getModuleName(user?.configs, ModuleType.Incident);
  const [searchKey]: [string] = props.ohsFormSettings.useFormMethods.watch(['searchKey']);
  const error = _.get(props.ohsFormSettings.useFormMethods.formState.errors, 'searchKey');
  const searchFieldError: string = error?.message as string;

  const getCategoryFilterName = (moduleType?: ModuleType) => {
    if (moduleType === ModuleType.Incident)
      return `${getModuleName(user?.configs, moduleType)} Type`;
    if (moduleType === ModuleType.Action) return 'Generated By';

    return 'Categories';
  };

  const categoryFilterName = getCategoryFilterName(props.moduleType);

  return (
    <Box sx={{ width: '100%', margin: 'auto' }}>
      <Form
        control={props.ohsFormSettings.useFormMethods.control}
        onSubmit={() => {
          ohsDebounce(props.ohsFormSettings.useFormMethods.handleSubmit(props.formSubmit));
        }}
      >
        <Box className={classes.card}>
          {/* Search */}
          {inputFields.searchKey && (
            <OutlinedInput
              error={!!error}
              color="warning"
              fullWidth
              placeholder="Search"
              endAdornment={
                <InputAdornment position="end">
                  {_.isNil(searchKey) || searchKey === '' ? (
                    <IconButton
                      type="button"
                      onClick={props.ohsFormSettings.useFormMethods.handleSubmit(props.formSubmit)}
                    >
                      <SearchIcon />
                    </IconButton>
                  ) : (
                    <IconButton
                      onClick={() => {
                        props.ohsFormSettings.useFormMethods.setValue('searchKey', '');
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  props.ohsFormSettings.useFormMethods.handleSubmit(props.formSubmit)();
                }
              }}
              {...props.ohsFormSettings.useFormMethods.register('searchKey', {
                required: false,
                validate: {
                  minLength: (value: any) => {
                    if (value.length < 3) return 'The length must not be less than 3.';
                    return true;
                  },
                },
              })}
            />
          )}
          {error && (
            <FormHelperText error id="accountId-error">
              {searchFieldError}
            </FormHelperText>
          )}
        </Box>
        <Box className={classes.filterCheckboxes}>
          <Grid container>
            {/* Substring */}
            {inputFields.substringSearch && (
              <Grid item md={6} xs={12} sm={6}>
                <OhsCheckboxFilter
                  fieldid="substringSearch"
                  label="Find search terms within longer words or phrases"
                  ohsFormSettings={props.ohsFormSettings}
                />
              </Grid>
            )}

            {/* Skip org */}
            {inputFields.skipOrgRecords && user?.tier.type === TierType.T3 && (
              <Grid item md={6} xs={12} sm={6}>
                <OhsCheckboxFilter
                  fieldid="filter.skipOrgRecords"
                  label="Skip Org Records"
                  ohsFormSettings={props.ohsFormSettings}
                />
              </Grid>
            )}

            {/* Allocated to Org */}
            {inputFields.allocatedToOrg && (
              <Grid item md={6} xs={12} sm={6}>
                <OhsCheckboxFilter
                  fieldid="filter.allocatedToOrg"
                  label="Allocated to Organisation"
                  ohsFormSettings={props.ohsFormSettings}
                />
              </Grid>
            )}

            {/* Allocated to Workplace */}
            {inputFields.allocatedToWorkplace && (
              <Grid item md={6} xs={12} sm={6}>
                <OhsCheckboxFilter
                  fieldid="filter.allocatedToWorkplace"
                  label="Allocated to Workplace"
                  ohsFormSettings={props.ohsFormSettings}
                />
              </Grid>
            )}

            {/* Not Allocated */}
            {inputFields.notAllocated && (
              <Grid item md={6} xs={12} sm={6}>
                <OhsCheckboxFilter
                  fieldid="filter.notAllocated"
                  label="Not Allocated"
                  ohsFormSettings={props.ohsFormSettings}
                />
              </Grid>
            )}

            {/* Workplace Owned */}
            {inputFields.workplaceOwned && (
              <Grid item md={6} xs={12} sm={6}>
                <OhsCheckboxFilter
                  fieldid="filter.workplaceOwned"
                  label="Workplace Owned"
                  ohsFormSettings={props.ohsFormSettings}
                />
              </Grid>
            )}

            {/* In Progress */}
            {props.inputFields.inProgress && (
              <Grid item md={6} xs={12} sm={6}>
                <OhsCheckboxFilter
                  fieldid="filter.inProgress"
                  label="In Progress"
                  ohsFormSettings={props.ohsFormSettings}
                />
              </Grid>
            )}
          </Grid>

          {/* Leave */}
          {inputFields.leave && (
            <Box>
              <OhsCheckboxFilter
                fieldid="filter.leave"
                label="Exclude leave records"
                ohsFormSettings={props.ohsFormSettings}
              />
            </Box>
          )}

          {/* Archived */}
          {inputFields.archived && user?.tierNum !== 5 && (
            <Box>
              <OhsCheckboxFilter
                fieldid="filter.archived"
                label="Archived"
                ohsFormSettings={props.ohsFormSettings}
              />
            </Box>
          )}
          {inputFields.includeTierNull && (
            <Box>
              <OhsCheckboxFilter
                fieldid="filter.includeTierNull"
                label="Include General Records"
                ohsFormSettings={props.ohsFormSettings}
              />
            </Box>
          )}
          {/* Prop Env Damage */}
          {inputFields.propOrEnvDamage && user?.tierNum !== 5 && (
            <Box>
              <OhsCheckboxFilter
                fieldid="filter.propOrEnvDamage"
                label="Property and/or Environmental Damage"
                ohsFormSettings={props.ohsFormSettings}
              />
            </Box>
          )}
        </Box>

        {/* Sort */}
        {inputFields.sortOptions && (
          <Box className={classes.card}>
            <OhsSortFilter
              required
              options={inputFields.sortOptions}
              id="sort"
              ohsFormSettings={props.ohsFormSettings}
            />
          </Box>
        )}
        <Box
          className={clsx({
            [classes.filterWrap]: true,
          })}
        >
          <Grid container spacing={2} p={1}>
            {/* Modules */}
            {inputFields.moduleOptions && (
              <Grid item md={inputFields.workplaces ? 6 : 12} xs={12}>
                <OhsModuleFilter
                  required={props.inputFields.requiredFields?.includes('modules')}
                  moduleOptionLists={inputFields.moduleOptions}
                  id="filter.modules"
                  ohsFormSettings={props.ohsFormSettings}
                />
              </Grid>
            )}

            {/* Workplace */}
            {inputFields.workplaces && (
              <Grid item md={inputFields.modules ? 6 : 12} xs={12}>
                <OhsWorkplaceFilter
                  required={props.inputFields.requiredFields?.includes('workplaces')}
                  id="filter.workplaces"
                  ohsFormSettings={props.ohsFormSettings}
                  workplaceOptions={props.inputFields.workplaceOptions || null}
                />
              </Grid>
            )}

            {/* Categories */}
            {inputFields.category && (
              <Grid item md={inputFields.modules ? 6 : 12} xs={12}>
                <OhsMultipleSelectField
                  title={returnSelectLabel(inputFields.category, categoryFilterName)}
                  id="filter.categories"
                  required={false}
                  options={returnSelectOptions(inputFields.category)}
                  ohsFormSettings={props.ohsFormSettings}
                />
              </Grid>
            )}

            {/* Subcategories */}
            {inputFields.subcategory && (
              <Grid item md={inputFields.modules ? 6 : 12} xs={12}>
                <OhsMultipleSelectField
                  title={returnSelectLabel(inputFields.subcategory, 'Subcategories')}
                  id="filter.subcategories"
                  required={false}
                  options={returnSelectOptions(inputFields.subcategory)}
                  ohsFormSettings={props.ohsFormSettings}
                />
              </Grid>
            )}

            {/* Status */}
            {inputFields.statusOptions && (
              <Grid item md={inputFields.modules ? 6 : 12} xs={12}>
                <OhsMultipleSelectField
                  title="Status"
                  id="filter.status"
                  required={false}
                  options={
                    inputFields.statusOptions.map((item: any) => {
                      return { name: item, value: item };
                    }) ?? []
                  }
                  ohsFormSettings={props.ohsFormSettings}
                />
              </Grid>
            )}

            {/* Severity */}
            {inputFields.severityOptions && (
              <Grid item md={inputFields.modules ? 6 : 12} xs={12}>
                <OhsMultipleSelectField
                  title="Risk"
                  id="filter.severities"
                  required={false}
                  options={
                    inputFields.severityOptions.map((item: any) => {
                      return { name: item, value: item };
                    }) ?? []
                  }
                  ohsFormSettings={props.ohsFormSettings}
                />
              </Grid>
            )}
          </Grid>

          {/* Skip Allocated */}
          <Box className={classes.filterCheckboxes}>
            {inputFields.skipAllocatedRecords && (
              <Box>
                <OhsCheckboxFilter
                  fieldid="filter.skipAllocatedRecords"
                  label="Skip Allocated Records"
                  ohsFormSettings={props.ohsFormSettings}
                />
              </Box>
            )}

            {/* Complete */}
            {inputFields?.task?.complete && (
              <Box>
                <OhsCheckboxFilter
                  fieldid="filter.task.complete"
                  label="Task Complete"
                  ohsFormSettings={props.ohsFormSettings}
                />
              </Box>
            )}

            {/* Task */}
            {inputFields?.task?.includeMasterRecords && (
              <Box>
                <OhsCheckboxFilter
                  fieldid="filter.task.includeMasterRecords"
                  label="Task Include Master Records"
                  ohsFormSettings={props.ohsFormSettings}
                />
              </Box>
            )}
          </Box>

          {/* Date Due */}
          {props.inputFields.dateDue && (
            <Box className={classes.card}>
              <OhsDateDueFilter
                fieldId={`filter.${ParamTypes.DateDue}`}
                typeId={`filter.${ParamTypes.DateDueType}`}
                label="Task Due Date"
                ohsFormSettings={props.ohsFormSettings}
                isSearch
                options={props.inputFields.dateDueOptions || []}
              />
            </Box>
          )}

          {/* Date Completed */}
          {props.inputFields.dateCompleted && (
            <Box className={classes.card}>
              <OhsDateRangePickerNew
                id={`filter.${ParamTypes.DateCompleted}`}
                label="Completed Date"
                ohsFormSettings={props.ohsFormSettings}
                maxDate={{ name: 'Today', date: getTodayDateString() }}
                isSearch
              />
            </Box>
          )}

          {/* Date Signoff */}
          {props.inputFields.dateSignedoff && (
            <Box className={classes.card}>
              <OhsDateRangePickerNew
                id={`filter.${ParamTypes.DateSignedoff}`}
                label="Sign-off Date"
                ohsFormSettings={props.ohsFormSettings}
                maxDate={{ name: 'Today', date: getTodayDateString() }}
                isSearch
              />
            </Box>
          )}

          {/* Date Created */}
          {props.inputFields.dateCreated && (
            <Box className={classes.card}>
              <OhsDateRangePickerNew
                id={`filter.${ParamTypes.DateCreated}`}
                label="Created Date"
                ohsFormSettings={props.ohsFormSettings}
                maxDate={{ name: 'Today', date: getTodayDateString() }}
                isSearch
              />
            </Box>
          )}

          {/* Date Identified */}
          {props.inputFields.dateIdentified && (
            <Box className={classes.card}>
              <OhsDateRangePickerNew
                id={`filter.${ParamTypes.DateIdentified}`}
                label="Date Identified"
                ohsFormSettings={props.ohsFormSettings}
                maxDate={{ name: 'Today', date: getTodayDateString() }}
                isSearch
              />
            </Box>
          )}

          {/* Date of Incident */}
          {props.inputFields.dateOccurred && (
            <Box className={classes.card}>
              <OhsDateRangePickerNew
                id={`filter.${ParamTypes.DateOccurred}`}
                label={`${OhsDateOccurredRecordField.name} ${moduleName}`}
                ohsFormSettings={props.ohsFormSettings}
                maxDate={{ name: 'Today', date: getTodayDateString() }}
                isSearch
              />
            </Box>
          )}

          {/* Date Issued */}
          {props.inputFields.dateIssued && (
            <Box className={classes.card}>
              <OhsDateRangePickerNew
                id={`filter.${ParamTypes.DateIssued}`}
                label="Date Issued"
                ohsFormSettings={props.ohsFormSettings}
                maxDate={{ name: 'Today', date: getTodayDateString() }}
                isSearch
              />
            </Box>
          )}

          {/* Date Expiry */}
          {props.inputFields.dateExpiry && (
            <Box className={classes.card}>
              <OhsDateRangePickerNew
                id={`filter.${ParamTypes.DateExpiry}`}
                label="Date Expiry"
                ohsFormSettings={props.ohsFormSettings}
                isSearch
              />
            </Box>
          )}
        </Box>
      </Form>
    </Box>
  );
}

OhsModuleSearchForm.defaultProps = {
  moduleType: undefined,
};

export default OhsModuleSearchForm;
